import Filter from './filter';
import { computeCurrencyMaxCharCount, computeNumberMaxCharCount, computePercentageMaxCharCount, processChildrenToMap, computeCumulativeMap } from "@/utils/transformers";
import { computeMinMax } from "@/utils";
import { request } from "@/store";
import moment from "moment";
import { FLOATING_COMPOUND_TABLE_STATES, GROUP_BY_TAB_ELEMENTS, APP_PERIOD_DROPDOWN_ELEMENTS } from "@/utils/constants";
import { DATE_PICKER_RANGE_TITLES, getHistoryPeriodSelector } from '../utils/datepicker';
import { Currencies } from '../utils/currencies';
import { PERIOD_OPTIONS, PERIOD_OPTIONS_V2 } from '../utils/constants';
const DEFAULT_EMPTY_ROW_SIZE = 20;
const market_insights = {
  state: {
    sellers_filter: new Filter([], []),
    marketplaces_filter: new Filter([], [], "marketplace_id"),
    categories_filter: new Filter([], []),
    currencies: new Filter(Currencies, [], "symbol"),
    period: APP_PERIOD_DROPDOWN_ELEMENTS.WEEKLY.value,
    periodSelectorOptions: [APP_PERIOD_DROPDOWN_ELEMENTS.WEEKLY, APP_PERIOD_DROPDOWN_ELEMENTS.MONTHLY, APP_PERIOD_DROPDOWN_ELEMENTS.QUARTERLY],
    periodCategory: PERIOD_OPTIONS_V2[APP_PERIOD_DROPDOWN_ELEMENTS.WEEKLY.value].LEFT.queryKey,
    product_table: {
      state: FLOATING_COMPOUND_TABLE_STATES.INTERMEDIATE,
      group_by: 0,
      group_by_text: "",
      group_by_elements: [],
      competitor_parents: new Map(),
      default: {
        period: null,
        data: {
          category: {
            items: new Array(DEFAULT_EMPTY_ROW_SIZE).fill({}),
            total: [],
            minMax: {},
            maxCharCount: {},
            page: 1,
            perPage: 20,
            totalPages: 0,
            sort: {},
            fetch: "marketInsightsFetchReportGroupedCategories"
          },
          seller: {
            items: new Array(DEFAULT_EMPTY_ROW_SIZE).fill({}),
            total: [],
            minMax: {},
            maxCharCount: {},
            page: 1,
            perPage: 20,
            totalPages: 0,
            sort: {},
            fetch: "marketInsightsFetchReportGroupedSellers"
          },
          marketplace: {
            items: new Array(DEFAULT_EMPTY_ROW_SIZE).fill({}),
            total: [],
            minMax: {},
            maxCharCount: {},
            page: 1,
            perPage: 20,
            totalPages: 0,
            sort: {},
            fetch: "marketInsightsFetchReportGroupedMarketplaces"
          },
          competitor: {
            items: new Array(DEFAULT_EMPTY_ROW_SIZE).fill({}),
            total: [],
            minMax: {},
            maxCharCount: {},
            page: 1,
            perPage: 20,
            totalPages: 0,
            sort: {},
            fetch: "marketInsightsFetchReportGroupedCompetitors"
          }
        }
      }
    },
    currentTab: 0,
    currentTabValue: "default",
    options: [{
      text: "Weekly",
      value: "weekly"
    }, {
      text: "Monthly",
      value: "monthly"
    }],
    tabs: [{
      text: "Default",
      value: "default",
      activeBg: "#F3EEFF",
      activeColor: "#423994",
      feature: 'default'
    }],
    periodOptions: [PERIOD_OPTIONS.PREV_PERIOD, PERIOD_OPTIONS.SAME_PERIOD_LY],
    currentPeriod: {
      [APP_PERIOD_DROPDOWN_ELEMENTS.WEEKLY.value]: {
        title: {
          prev_period: "Last week vs previous week",
          same_period_ly: "Last week vs same period last year"
        },
        format: "MMM D, YYYY",
        date: moment().startOf("isoWeek").subtract(1, 'week'),
        groupBy: DATE_PICKER_RANGE_TITLES.LAST_WEEK,
        groupByCompare: {
          title: 'Preceding Period',
          subtitle: '(match to date)'
        }
      },
      [APP_PERIOD_DROPDOWN_ELEMENTS.MONTHLY.value]: {
        title: {
          prev_period: "Current month vs previous month",
          same_period_ly: "Current month vs same period last year"
        },
        format: "MMM YYYY",
        date: moment().startOf("month"),
        groupBy: DATE_PICKER_RANGE_TITLES.CURRENT_MONTH,
        groupByCompare: {
          title: 'Preceding Period',
          subtitle: '(match to date)'
        }
      },
      [APP_PERIOD_DROPDOWN_ELEMENTS.QUARTERLY.value]: {
        title: {
          prev_period: "Current quarter vs previous quarter",
          same_period_ly: "Current quarter vs same period last year"
        },
        format: "MMM YYYY",
        date: moment().startOf("quarter"),
        groupBy: DATE_PICKER_RANGE_TITLES.CURRENT_QUARTER,
        groupByCompare: {
          title: 'Preceding Period',
          subtitle: '(match to date)'
        }
      }
    },
    periodTable: {
      default: {
        items: new Array(DEFAULT_EMPTY_ROW_SIZE).fill({}),
        maxCharCount: {},
        minMax: {}
      },
      period_page: 1
    },
    currencyConfig: {
      full: false,
      currency: "EUR",
      precision: 0,
      showCurrencySign: true,
      digitSeparator: ".",
      limit: 10_000
    }
  },
  mutations: {
    marketInsightsSellersFilterSet(state, payload) {
      state.sellers_filter.restore(payload);
    },
    marketInsightsMarketplacesFilterSet(state, payload) {
      state.marketplaces_filter.restore(payload);
    },
    marketInsightsCategoriesFilterSet(state, payload) {
      state.categories_filter = payload;
    },
    marketInsightsSetPeriod(state, payload) {
      state.period = payload;
    },
    marketInsightsSetPeriodCategory(state, payload) {
      state.periodCategory = payload;
    },
    marketInsightsSetCurrentTab(state, payload) {
      const {
        tabIndex,
        tabValue
      } = payload;
      state.currentTab = tabIndex;
      state.currentTabValue = tabValue;
    },
    marketInsightsSetPeriodTableItems(state, payload) {
      state.periodTable['default'].items = payload;
    },
    marketInsightsDeselectPeriodTable(state) {
      state.tabs.forEach(({
        value
      }) => {
        (state.periodTable[value]?.items ?? []).forEach(item => {
          item.selected = undefined;
        });
      });
    },
    marketInsightsSetPeriodTableMinMax(state, payload) {
      state.periodTable['default'].minMax = payload;
    },
    marketInsightsSetPeriodTableMaxCharCount(state, payload) {
      state.periodTable['default'].maxCharCount = payload;
    },
    marketInsightsSetProductTableMinMax(state, payload) {
      state.product_table['default'].data[state.product_table.group_by_text].minMax = payload;
    },
    marketInsightsSetProductTableTotal(state, payload) {
      state.product_table['default'].data[state.product_table.group_by_text].total = payload;
    },
    marketInsightsSetProductTableTotalPages(state, payload) {
      state.product_table['default'].data[state.product_table.group_by_text].totalPages = payload;
    },
    marketInsightsSetProductTablePage(state, payload) {
      state.product_table['default'].data[state.product_table.group_by_text].page = payload;
    },
    marketInsightsSetProductTablePerPage(state, payload) {
      state.product_table['default'].data[state.product_table.group_by_text].perPage = payload;
    },
    marketInsightsSetProductTableGroupBy(state, payload) {
      state.product_table.group_by = payload;
      state.product_table.group_by_text = state.product_table.group_by_elements[payload].key;
    },
    marketInsightsSetProductTablePeriod(state, payload) {
      state.product_table['default'].period = payload;
    },
    marketInsightsSetProductTableItems(state, payload) {
      state.product_table['default'].data[state.product_table.group_by_text].items = payload;
    },
    marketInsightsSetProductTableMaxCharCount(state, payload) {
      state.product_table['default'].data[state.product_table.group_by_text].maxCharCount = payload;
    },
    marketInsightsSetProductTableState(state, payload) {
      state.product_table.state = payload;
    },
    marketInsightsSetProductTableGroupByElements(state, payload) {
      state.product_table.group_by_elements = payload;
    },
    marketInsightsSetProductTableSort(state, {
      sort,
      direction
    }) {
      state.product_table['default'].data[state.product_table.group_by_text].sort = {
        [sort]: direction
      };
    },
    marketInsightsIncrementPeriodPage(state) {
      state.periodTable.period_page += 1;
    },
    marketInsightsDefaultCurrencySet(state, payload) {
      // We need to update the currency filter
      state.currencies.selectOnly({
        symbol: payload
      }, 'symbol');
      state.currencies.apply();
    },
    marketInsightsToggleCompetitorState(state, payload) {
      // Toggle All Off
      for (const [brand] of state.product_table.competitor_parents) {
        const brandChildrenLength = state.product_table.competitor_parents.get(brand)?.children_length;
        if (brand !== payload) state.product_table.competitor_parents.set(brand, {
          collapsed: false,
          view_all: false,
          children_length: brandChildrenLength
        });
      }
      const currentState = state.product_table.competitor_parents.get(payload);
      currentState.collapsed = !currentState.collapsed;
      if (!currentState.collapsed && currentState.view_all) {
        currentState.view_all = false;
      }
    },
    marketInsightsSetCompetitorParents(state, payload) {
      state.product_table.competitor_parents = payload;
    },
    marketInsightsToggleCompetitorViewAllState(state, payload) {
      const currentState = state.product_table.competitor_parents.get(payload);
      currentState.view_all = true;
    }
  },
  getters: {
    marketInsightsGetFilters(state) {
      return () => {
        return {
          seller_id: state.sellers_filter.filterValues,
          marketplaces: state.marketplaces_filter.selectedValues,
          categories: state.categories_filter.selectedValues
        };
      };
    },
    marketInsightsGetPeriod(state) {
      return () => state.period;
    },
    marketInsightsGetPeriodCategory(state) {
      return () => state.periodCategory;
    },
    marketInsightsGetTab(state) {
      return () => state.currentTab;
    },
    marketInsightsGetTabValue(state) {
      return () => state.currentTab;
    },
    marketInsightsGetProductTablePage(state) {
      return () => state.product_table[state.currentTabValue].data?.[state.product_table.group_by_text]?.page;
    },
    marketInsightsGetProductTablePerPage(state) {
      return () => state.product_table[state.currentTabValue].data?.[state.product_table.group_by_text]?.perPage;
    },
    marketInsightsGetProductTableTotalPages(state) {
      return () => state.product_table[state.currentTabValue].data?.[state.product_table.group_by_text]?.totalPages;
    },
    marketInsightsGetAccountState(state) {
      return () => {
        const res = {
          sellers: state.sellers_filter.defaultList,
          marketplaces: state.marketplaces_filter.defaultList
        };
        return res;
      };
    },
    marketInsightsGetProductTableSort(state) {
      return () => {
        let obj = {
          sort_by: [],
          sort_direction: []
        };
        for (const [key, value] of Object.entries(state.product_table[state.currentTabValue].data?.[state.product_table.group_by_text]?.sort)) {
          obj['sort_by'].push(key);
          obj['sort_direction'].push(value);
        }
        return obj;
      };
    },
    marketInsightsGetProductTablePager(state) {
      return () => ({
        page: state.product_table[state.currentTabValue].data?.[state.product_table.group_by_text]?.page,
        page_size: state.product_table[state.currentTabValue].data?.[state.product_table.group_by_text]?.perPage
      });
    },
    marketInsightsGetProductTableGroupBy(state) {
      return () => state.product_table.group_by;
    },
    marketInsightsSingleMarketplace(state) {
      return () => {
        const filteredMarketplaces = state.marketplaces_filter.filterValues;
        return filteredMarketplaces.length === 1;
      };
    },
    marketInsightsGetPeriodOptions(state) {
      const currentPeriod = state.period;
      return [PERIOD_OPTIONS_V2[currentPeriod].LEFT, PERIOD_OPTIONS_V2[currentPeriod].RIGHT];
    }
  },
  actions: {
    async marketInsightsFetchPeriodTableData({
      commit,
      getters,
      state
    }) {
      try {
        const dateConfig = getters.dateConfigQueryGet();
        const params = {
          period: state.period,
          ...getters.marketInsightsGetFilters(),
          ...getters.currencyConfigQueryGet(),
          ...dateConfig,
          period_page: state.periodTable.period_page
        };
        const {
          full,
          currency,
          precision,
          showCurrencySign,
          limit
        } = getters.currencyConfigGet();
        let response = await request('/market_insights/evolution', 'POST', params);
        let data = response.data;
        if (data) {
          commit("marketInsightsSetPeriodTableItems", data);
          commit('marketInsightsSetPeriodTableMaxCharCount', {
            currency: computeCurrencyMaxCharCount(data, full, currency, precision, showCurrencySign, false, limit),
            number: computeNumberMaxCharCount(data),
            percentage: computePercentageMaxCharCount(data)
          });
          commit("marketInsightsSetPeriodTableMinMax", computeMinMax(data));
        }
      } catch (err) {
        //console.log(err);
      }
    },
    async marketInsightsFetchReportGroupedCategories({
      commit,
      getters,
      state
    }, options = {}) {
      try {
        let {
          pager,
          store = true
        } = options;
        if (!pager) {
          pager = getters.marketInsightsGetProductTablePager();
        }
        const params = {
          period: state.product_table[state.currentTabValue].period,
          ...getters.marketInsightsGetFilters(),
          ...getters.marketInsightsGetProductTableSort(),
          ...pager,
          ...getters.currencyConfigQueryGet()
        };
        const {
          full,
          currency,
          precision,
          showCurrencySign,
          limit
        } = getters.currencyConfigGet();
        let response = await request("/market_insights/report/category", 'POST', params, false, true, 'cors');
        if (response) {
          response = response.data;
          const {
            report,
            total
          } = response;
          if (store) {
            commit('marketInsightsSetProductTableTotalPages', total?.[0]?.row_count ?? 0);
            commit('marketInsightsSetProductTableItems', report);
            commit('marketInsightsSetProductTableTotal', total?.pop() ?? {});
            commit('marketInsightsSetProductTableMaxCharCount', {
              currency: computeCurrencyMaxCharCount(report, full, currency, precision, showCurrencySign, false, limit),
              number: computeNumberMaxCharCount(report),
              percentage: computePercentageMaxCharCount(report)
            });
            commit('marketInsightsSetProductTableMinMax', computeMinMax(report));
          }
        }
        return response;
      } catch (err) {
        //console.log(err);
      }
    },
    async marketInsightsFetchReportGroupedSellers({
      commit,
      getters,
      state
    }, options = {}) {
      try {
        let {
          pager,
          store = true
        } = options;
        if (!pager) {
          pager = getters.marketInsightsGetProductTablePager();
        }
        const params = {
          period: state.product_table[state.currentTabValue].period,
          ...getters.marketInsightsGetFilters(),
          ...getters.marketInsightsGetProductTableSort(),
          ...pager,
          ...getters.currencyConfigQueryGet()
        };
        const {
          full,
          currency,
          precision,
          showCurrencySign,
          limit
        } = getters.currencyConfigGet();
        let response = await request("/market_insights/report/seller", 'POST', params, false, true, 'cors');
        if (response) {
          response = response.data;
          const {
            report,
            total
          } = response;
          if (store) {
            commit('marketInsightsSetProductTableTotalPages', total?.[0]?.row_count ?? 0);
            commit('marketInsightsSetProductTableItems', report);
            commit('marketInsightsSetProductTableTotal', total?.pop() ?? {});
            commit('marketInsightsSetProductTableMaxCharCount', {
              currency: computeCurrencyMaxCharCount(report, full, currency, precision, showCurrencySign, false, limit),
              number: computeNumberMaxCharCount(report),
              percentage: computePercentageMaxCharCount(report)
            });
            commit('marketInsightsSetProductTableMinMax', computeMinMax(report));
          }
        }
        return response;
      } catch (err) {
        //console.log(err);
      }
    },
    async marketInsightsFetchReportGroupedMarketplaces({
      commit,
      getters,
      state
    }, options = {}) {
      try {
        let {
          pager,
          store = true
        } = options;
        if (!pager) {
          pager = getters.marketInsightsGetProductTablePager();
        }
        const params = {
          period: state.product_table[state.currentTabValue].period,
          ...getters.marketInsightsGetFilters(),
          ...getters.marketInsightsGetProductTableSort(),
          ...pager,
          ...getters.currencyConfigQueryGet()
        };
        const {
          full,
          currency,
          precision,
          showCurrencySign,
          limit
        } = getters.currencyConfigGet();
        let response = await request("/market_insights/report/marketplace", 'POST', params, false, true, 'cors');
        if (response) {
          response = response.data;
          const {
            report,
            total
          } = response;
          if (store) {
            commit('marketInsightsSetProductTableTotalPages', total?.[0]?.row_count ?? 0);
            commit('marketInsightsSetProductTableItems', report);
            commit('marketInsightsSetProductTableTotal', total?.pop() ?? {});
            commit('marketInsightsSetProductTableMaxCharCount', {
              currency: computeCurrencyMaxCharCount(report, full, currency, precision, showCurrencySign, false, limit),
              number: computeNumberMaxCharCount(report),
              percentage: computePercentageMaxCharCount(report)
            });
            commit('marketInsightsSetProductTableMinMax', computeMinMax(report));
          }
        }
        return response;
      } catch (err) {
        //console.log(err);
      }
    },
    async marketInsightsFetchReportGroupedCompetitors({
      commit,
      getters,
      state
    }, options = {}) {
      try {
        let {
          pager,
          store = true
        } = options;
        if (!pager) {
          pager = getters.marketInsightsGetProductTablePager();
        }
        const params = {
          period: state.product_table[state.currentTabValue].period,
          ...getters.marketInsightsGetFilters(),
          ...getters.marketInsightsGetProductTableSort(),
          ...pager,
          ...getters.currencyConfigQueryGet()
        };
        const {
          full,
          currency,
          precision,
          showCurrencySign,
          limit
        } = getters.currencyConfigGet();
        let response = await request("/market_insights/report/competitor", 'POST', params, false, true, 'cors');
        if (response) {
          response = response.data;
          const {
            report,
            total
          } = response;
          const {
            productMap,
            processedResult
          } = processChildrenToMap(report, item => item.brand, "asin_list");
          // const productMap = computeCumulativeMap(report);
          if (store) {
            commit('marketInsightsSetCompetitorParents', productMap);
            commit('marketInsightsSetProductTableTotalPages', total?.[0]?.row_count ?? 0);
            commit('marketInsightsSetProductTableItems', processedResult);
            commit('marketInsightsSetProductTableTotal', total?.pop() ?? {});
            commit('marketInsightsSetProductTableMaxCharCount', {
              currency: computeCurrencyMaxCharCount(processedResult, full, currency, precision, showCurrencySign, false, limit),
              number: computeNumberMaxCharCount(processedResult),
              percentage: computePercentageMaxCharCount(processedResult)
            });
            commit('marketInsightsSetProductTableMinMax', computeMinMax(processedResult));
          }
        }
        return response;
      } catch (err) {
        //console.log(err);
      }
    },
    marketInsightsSetPeriod({
      commit
    }, period) {
      commit('marketInsightsSetPeriod', period);
    },
    marketInsightsSetPeriodCategory({
      commit
    }, periodCategory) {
      commit('marketInsightsSetPeriodCategory', periodCategory);
    },
    marketInsightsSetTab({
      commit
    }, tab) {
      commit('marketInsightsSetCurrentTab', tab);
    },
    marketInsightsSetProductTableGroupBy({
      commit
    }, groupBy) {
      commit('marketInsightsSetProductTableGroupBy', groupBy);
    },
    marketInsightsSetProductTablePeriod({
      commit
    }, period) {
      commit('marketInsightsSetProductTablePeriod', period);
    },
    marketInsightsSetProductTableState({
      commit
    }, payload) {
      commit('marketInsightsSetProductTableState', payload);
    },
    marketInsightsSetProductTablePage({
      commit
    }, page) {
      commit('marketInsightsSetProductTablePage', page);
    },
    marketInsightsSetProductTablePerPage({
      commit
    }, perPage) {
      commit('marketInsightsSetProductTablePerPage', perPage);
    },
    marketInsightsSetProductTableTotalPages({
      commit
    }, totalPages) {
      commit('marketInsightsSetProductTableTotalPages', totalPages);
    },
    marketInsightsSetProductTableGroupByElements({
      commit,
      getters
    }) {
      const {
        sellers,
        marketplaces
      } = getters.marketInsightsGetAccountState();
      const {
        SELLER,
        MARKETPLACE,
        PRODUCT_SKU,
        PRODUCT_ASIN,
        PRODUCT_PARENT,
        SEGMENT,
        BRAND,
        CATEGORY,
        COMPETITOR
      } = GROUP_BY_TAB_ELEMENTS;
      const DEFAULT_TABS = [CATEGORY, COMPETITOR];
      const prefixTabs = [];
      if (sellers.length > 1) {
        prefixTabs.push(SELLER);
      }
      if (marketplaces.length > 1) {
        prefixTabs.push(MARKETPLACE);
      }
      const suffixTabs = [...DEFAULT_TABS];
      const finalTabs = [...prefixTabs, ...suffixTabs];
      commit("marketInsightsSetProductTableGroupByElements", finalTabs);
      commit("marketInsightsSetProductTableGroupBy", finalTabs.indexOf(CATEGORY));
    },
    marketInsightsSetProductTableSort({
      commit
    }, payload) {
      commit('marketInsightsSetProductTableSort', payload);
    },
    marketInsightsIncrementPeriodPage({
      commit
    }) {
      commit('marketInsightsIncrementPeriodPage');
    },
    marketInsightsSetDefaultCurrency({
      getters,
      commit
    }) {
      //console.log('getters', getters.currencyConfigQueryGet())
      commit('marketInsightsDefaultCurrencySet', getters.currencyConfigQueryGet().currency);
    },
    navigateToGroupBy({
      state,
      commit
    }, groupByElement) {
      const index = state.product_table.group_by_elements.findIndex(obj => groupByElement.name === obj.name);
      if (index === -1) return;
      commit("marketInsightsSetProductTableGroupBy", index);
    },
    marketInsightsToggleCompetitorState({
      commit
    }, payload) {
      //console.log('payload', payload)
      commit('marketInsightsToggleCompetitorState', payload);
    },
    marketInsightsToggleCompetitorViewAllState({
      commit
    }, payload) {
      commit('marketInsightsToggleCompetitorViewAllState', payload);
    },
    async getMarketInsightsCategories({
      state
    }, payload) {
      const result = await request("/market_insights/filters/categories", 'POST', payload, false, true, 'cors');
      if (result && result.data && result.data.categories) {
        let categories = result.data.categories;
        return categories.map(({
          category_id,
          category_name
        }) => ({
          id: category_id,
          category: category_name
        }));
      }
    }
  }
};
export default market_insights;